import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, Bell, MessageSquare, ChevronDown, User, Settings, LogOut, Eye, EyeOff, Shield, Crown, Users, ArrowLeft } from 'lucide-react';

import BrandLogo from '../ui/BrandLogo';
import ThemeToggle from '../ui/ThemeToggle';
import UnifiedButton from './UnifiedButton';
import Tooltip from '../ui/Tooltip';
import { icons, colors } from '../../styles/design-tokens';
import { useMobileResponsive } from '../../hooks/useMobileResponsive';

/**
 * UnifiedHeader Component
 * Standard header component with variants for different contexts
 * 
 * Variants:
 * - 'public': Public-facing pages (home, marketing)
 * - 'auth': Authentication pages (login, register)
 * - 'dashboard': Dashboard pages with full user controls
 * 
 * @param {string} variant - Header variant
 * @param {function} onToggleSidebar - Sidebar toggle handler (dashboard only)
 * @param {boolean} sidebarCollapsed - Sidebar state (dashboard only)
 * @param {object} companyData - Company info for company dashboard
 * @param {string} userRole - User role (admin/recruiter) for company dashboard
 * @param {object} user - User data for dashboard
 * @param {array} notifications - Notification data for dashboard
 * @param {array} messages - Message data for dashboard
 * @param {string} className - Additional CSS classes
 */
const UnifiedHeader = ({ 
  variant = 'public',
  onToggleSidebar,
  sidebarCollapsed = false,
  companyData = null,
  userRole = null,
  user = null,
  notifications = [],
  messages = [],
  className = '',
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isMobile } = useMobileResponsive();
  
  // Dropdown states
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [messagesOpen, setMessagesOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [visibilityStatus, setVisibilityStatus] = useState('online');
  const [openDropdown, setOpenDropdown] = useState(null);
  
  // Refs for click outside detection
  const notificationsRef = useRef(null);
  const messagesRef = useRef(null);
  const profileRef = useRef(null);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notificationsRef?.current && !notificationsRef?.current?.contains(event?.target)) {
        setNotificationsOpen(false);
      }
      if (messagesRef?.current && !messagesRef?.current?.contains(event?.target)) {
        setMessagesOpen(false);
      }
      if (profileRef?.current && !profileRef?.current?.contains(event?.target)) {
        setProfileOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Animation variants
  const dropdownVariants = {
    hidden: { opacity: 0, scale: 0.95, y: -10 },
    visible: { 
      opacity: 1, 
      scale: 1, 
      y: 0,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 30,
      }
    },
    exit: { 
      opacity: 0, 
      scale: 0.95, 
      y: -10,
      transition: { duration: 0.15 }
    }
  };

  // Helper functions
  const getVisibilityIcon = (status) => {
    switch (status) {
      case 'online': return <Eye className={icons?.sm} />;
      case 'offline': return <EyeOff className={icons?.sm} />;
      case 'hidden': return <Shield className={icons?.sm} />;
      default: return <Eye className={icons?.sm} />;
    }
  };

  const getVisibilityLabel = (status) => {
    switch (status) {
      case 'online': return 'Online';
      case 'offline': return 'Offline';
      case 'hidden': return 'Hidden';
      default: return 'Online';
    }
  };

  const getVisibilityColor = (status) => {
    switch (status) {
      case 'online': return 'bg-green-500';
      case 'offline': return 'bg-gray-400';
      case 'hidden': return 'bg-yellow-500';
      default: return 'bg-green-500';
    }
  };

  const getRoleIcon = (role) => {
    return role === 'admin' ? <Crown className={icons?.xs} /> : <Users className={icons?.xs} />;
  };

  const getRoleLabel = (role) => {
    return role === 'admin' ? 'Admin' : 'Recruiter';
  };

  const getRoleColor = (role) => {
    return role === 'admin' ? 'text-yellow-600' : 'text-blue-600';
  };

  // Render variant-specific content
  const renderPublicVariant = () => (
    <>
      <BrandLogo size="lg" showIcon={true} showText={true} linkTo="/" />
      <div className="flex items-center space-x-6">
        <ThemeToggle />
        <UnifiedButton 
          variant="ghost" 
          onClick={() => navigate('/login')}
        >
          Sign in
        </UnifiedButton>
        <UnifiedButton 
          variant="ghost"
          onClick={() => navigate('/for-employers-landing-page')}
        >
          For Employers
        </UnifiedButton>
      </div>
    </>
  );

  const renderAuthVariant = () => (
    <>
      <BrandLogo size="lg" showIcon={true} showText={true} linkTo="/" />
      <div className="flex items-center space-x-4">
        <ThemeToggle />
        {location?.pathname === '/login' ? (
          <UnifiedButton 
            variant="ghost" 
            onClick={() => navigate('/register')}
          >
            Create Account
          </UnifiedButton>
        ) : (
          <UnifiedButton 
            variant="ghost" 
            onClick={() => navigate('/login')}
          >
            Sign In
          </UnifiedButton>
        )}
      </div>
    </>
  );

  const renderDashboardVariant = () => (
    <>
      {/* Left Section */}
      <div className="flex items-center space-x-2 md:space-x-4">
        {/* Mobile Hamburger Menu - 44px minimum touch target */}
        <button
          onClick={onToggleSidebar}
          className="min-w-[44px] min-h-[44px] flex items-center justify-center rounded-lg hover:bg-accent/10 transition-colors touch-manipulation active:scale-95"
          aria-label="Toggle sidebar"
        >
          <Menu className="w-5 h-5 text-foreground" />
        </button>

        {/* Logo */}
        <BrandLogo 
          linkTo="/dashboard" 
          size={isMobile ? 'sm' : 'default'}
          showText={!isMobile}
          className="transition-all duration-300"
        />
      </div>

      {/* Right Section */}
      <div className="flex items-center space-x-3">
        {/* Notifications Dropdown */}
        <div className="relative" ref={notificationsRef}>
          <Tooltip content="Notifications">
            <UnifiedButton
              variant="ghost"
              size="icon"
              onClick={() => setNotificationsOpen(!notificationsOpen)}
              className="relative"
            >
              <Bell className={icons?.md} />
              {notifications?.filter(n => n?.unread)?.length > 0 && (
                <span className={`absolute -top-1 -right-1 ${icons?.xs} bg-red-500 rounded-full flex items-center justify-center text-[10px] text-white font-bold`}>
                  {notifications?.filter(n => n?.unread)?.length}
                </span>
              )}
            </UnifiedButton>
          </Tooltip>

          <AnimatePresence>
            {notificationsOpen && (
              <motion.div
                variants={dropdownVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                className="absolute right-0 top-full mt-2 w-96 bg-background border border-border rounded-lg shadow-lg overflow-hidden z-50"
              >
                <div className="p-4 border-b border-border">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-foreground">Notifications</h3>
                    <UnifiedButton variant="link" size="sm">
                      See all
                    </UnifiedButton>
                  </div>
                </div>
                <div className="max-h-80 overflow-y-auto">
                  {notifications?.slice(0, 3)?.map(notification => (
                    <div
                      key={notification?.id}
                      className={`p-4 border-b border-border last:border-b-0 hover:bg-muted/50 cursor-pointer ${
                        notification?.unread ? 'bg-accent/5' : ''
                      }`}
                    >
                      <div className="flex items-start space-x-3">
                        <div className={`w-2 h-2 rounded-full mt-2 ${notification?.unread ? 'bg-accent' : 'bg-transparent'}`} />
                        <div className="flex-1 min-w-0">
                          <h4 className="text-sm font-medium text-foreground truncate">
                            {notification?.title}
                          </h4>
                          <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                            {notification?.message}
                          </p>
                          <span className="text-xs text-muted-foreground mt-2 block">
                            {notification?.time}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                  {notifications?.length === 0 && (
                    <div className="p-8 text-center text-sm text-muted-foreground">
                      No notifications yet
                    </div>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Messages Dropdown */}
        <div className="relative" ref={messagesRef}>
          <Tooltip content="Messages">
            <UnifiedButton
              variant="ghost"
              size="icon"
              onClick={() => setMessagesOpen(!messagesOpen)}
              className="relative"
            >
              <MessageSquare className={icons?.md} />
              {messages?.length > 0 && (
                <span className={`absolute -top-1 -right-1 ${icons?.xs} bg-accent rounded-full`} />
              )}
            </UnifiedButton>
          </Tooltip>

          <AnimatePresence>
            {messagesOpen && (
              <motion.div
                variants={dropdownVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                className="absolute right-0 top-full mt-2 w-96 bg-background border border-border rounded-lg shadow-lg overflow-hidden z-50"
              >
                <div className="p-4 border-b border-border">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-foreground">Messages</h3>
                    <UnifiedButton variant="link" size="sm">
                      See all
                    </UnifiedButton>
                  </div>
                </div>
                <div className="max-h-80 overflow-y-auto">
                  {messages?.slice(0, 3)?.map(message => (
                    <div
                      key={message?.id}
                      className="p-4 border-b border-border last:border-b-0 hover:bg-muted/50 cursor-pointer"
                    >
                      <div className="flex items-start space-x-3">
                        <div className="w-8 h-8 bg-accent text-accent-foreground rounded-full flex items-center justify-center text-sm font-semibold">
                          {message?.avatar}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-2">
                            <h4 className="text-sm font-medium text-foreground truncate">
                              {message?.from}
                            </h4>
                            {message?.type && (
                              <span className={`text-xs px-2 py-0.5 rounded-full ${
                                message?.type === 'candidate' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'
                              }`}>
                                {message?.type}
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                            {message?.message}
                          </p>
                          <span className="text-xs text-muted-foreground mt-2 block">
                            {message?.time}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                  {messages?.length === 0 && (
                    <div className="p-8 text-center text-sm text-muted-foreground">
                      No messages yet
                    </div>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Profile Dropdown */}
        <div className="relative" ref={profileRef}>
          <UnifiedButton
            variant="ghost"
            onClick={() => setProfileOpen(!profileOpen)}
            className="flex items-center space-x-2 px-3 py-2 h-auto"
          >
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-accent text-accent-foreground rounded-full flex items-center justify-center text-sm font-semibold">
                {user?.initials || 'JD'}
              </div>
              <div className="hidden sm:block text-left">
                <div className="text-sm font-medium text-foreground">{user?.name || 'John Doe'}</div>
                <div className="flex items-center space-x-1">
                  {companyData ? (
                    <>
                      {getRoleIcon(userRole)}
                      <span className={`text-xs font-medium ${getRoleColor(userRole)}`}>
                        {getRoleLabel(userRole)}
                      </span>
                    </>
                  ) : (
                    <>
                      <div className={`w-2 h-2 rounded-full ${getVisibilityColor(visibilityStatus)}`} />
                      <span className="text-xs text-muted-foreground">{getVisibilityLabel(visibilityStatus)}</span>
                    </>
                  )}
                </div>
              </div>
              <ChevronDown className={`${icons?.sm} text-muted-foreground transition-transform ${profileOpen ? 'rotate-180' : ''}`} />
            </div>
          </UnifiedButton>

          <AnimatePresence>
            {profileOpen && (
              <motion.div
                variants={dropdownVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                className="absolute right-0 top-full mt-2 w-64 bg-background border border-border rounded-lg shadow-lg overflow-hidden z-50"
              >
                <div className="p-4 border-b border-border">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-accent text-accent-foreground rounded-full flex items-center justify-center text-sm font-semibold">
                      {user?.initials || 'JD'}
                    </div>
                    <div>
                      <div className="font-medium text-foreground">{user?.name || 'John Doe'}</div>
                      <div className="text-sm text-muted-foreground">{user?.email || 'john.doe@example.com'}</div>
                    </div>
                  </div>
                </div>

                <div className="py-2">
                  {companyData && (
                    <button
                      onClick={() => navigate('/dashboard')}
                      className="flex items-center space-x-3 w-full px-4 py-2 text-left hover:bg-muted/50 transition-colors"
                    >
                      <ArrowLeft className={`${icons?.sm} text-muted-foreground`} />
                      <span className="text-sm">Switch to Job Seeker</span>
                    </button>
                  )}

                  <button
                    onClick={() => navigate(companyData ? `/company-dashboard-hub/${companyData?.id}/profile` : '/dashboard/profile')}
                    className="flex items-center space-x-3 w-full px-4 py-2 text-left hover:bg-muted/50 transition-colors"
                  >
                    <User className={`${icons?.sm} text-muted-foreground`} />
                    <span className="text-sm">Profile</span>
                  </button>

                  <button
                    onClick={() => navigate(companyData ? `/company-dashboard-hub/${companyData?.id}/profile?tab=settings` : '/dashboard/profile?tab=preferences')}
                    className="flex items-center space-x-3 w-full px-4 py-2 text-left hover:bg-muted/50 transition-colors"
                  >
                    <Settings className={`${icons?.sm} text-muted-foreground`} />
                    <span className="text-sm">{companyData ? 'Settings' : 'Preferences'}</span>
                  </button>

                  {!companyData && (
                    <div className="px-4 py-2 border-t border-border mt-2">
                      <div className="text-xs font-medium text-muted-foreground mb-2">Privacy/Visibility</div>
                      <div className="space-y-1">
                        {['online', 'offline', 'hidden']?.map((status) => (
                          <button
                            key={status}
                            onClick={() => setVisibilityStatus(status)}
                            className={`flex items-center space-x-2 w-full px-2 py-1 rounded text-left text-sm transition-colors ${
                              visibilityStatus === status 
                                ? 'bg-accent/10 text-accent' :'hover:bg-muted/50 text-muted-foreground'
                            }`}
                          >
                            {getVisibilityIcon(status)}
                            <span className="capitalize">{status}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="border-t border-border mt-2">
                    <button
                      onClick={() => navigate('/login')}
                      className="flex items-center space-x-3 w-full px-4 py-2 text-left hover:bg-destructive/10 hover:text-destructive transition-colors"
                    >
                      <LogOut className={icons?.sm} />
                      <span className="text-sm">Logout</span>
                    </button>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </>
  );

  const renderContent = () => {
    switch (variant) {
      case 'public':
        return renderPublicVariant();
      case 'auth':
        return renderAuthVariant();
      case 'dashboard':
        return renderDashboardVariant();
      default:
        return renderPublicVariant();
    }
  };

  return (
    <header className={`fixed top-0 left-0 right-0 z-40 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b border-border ${className}`}>
      <div className="flex items-center justify-between h-16 px-6">
        {renderContent()}
      </div>
    </header>
  );
};

export default UnifiedHeader;